/* Funsquare realism pilot. Only installed in the authorised unpublished theme. */
export const normaliseMode=value=>['original','materials','refined'].includes(value)?value:'refined';
export const profileFor=fabric=>fabric==='corduroy'
  ? {metalness:0,roughness:.94,normalStrength:1.15,sheen:.12}
  : {metalness:0,roughness:.98,normalStrength:.32,sheen:.18};
export const effectiveMode=(mode,fabric)=>fabric==='microsuede'?'original':normaliseMode(mode);
export const modelFor=(mode,config,fabric='corduroy')=>effectiveMode(mode,fabric)==='original'?config.model.glbUrl:config.realism.modelUrl;
export function colourKeyForNode(name,state){
  if(/^base.*Top$/.test(name))return state.upperBase;
  if(/^base.*Bottom$/.test(name))return state.lowerBase;
  if(name==='Trapezium')return state.trapezium;
  if(name.startsWith('Armrest_'))return state.armrest.colour;
  if(name.startsWith('extra'))return state.extraFun.colour;
  if(name==='CircleCushion1')return state.dotCushion.cushion1.fabric;
  if(name==='CircleCushion2')return state.dotCushion.cushion2.fabric;
  return null;
}

export function createRealism(root,config,store){
  const viewer=root.querySelector('#couch-viewer');
  const panel=root.querySelector('[data-realism-panel]');
  const status=root.querySelector('[data-realism-status]');
  let mode='materials';
  const currentMode=()=>effectiveMode(mode,store.getState().fabricType);
  let nodes=null,repaint=null,loading=false;
  const cache=new Map(),pending=new Map();
  const palette=new Map(config.fabricColours.map(c=>[c.key,c.hex]));
  const getScene=()=>viewer[Object.getOwnPropertySymbols(viewer).find(s=>s.description==='scene')];
  let framedParts='';
  async function resetCamera(){
    const s=store.getState(),hasExtra=s.extraFun.type!=='none';
    if(s.fabricType==='microsuede'){
      viewer.removeAttribute('camera-target');viewer.removeAttribute('field-of-view');
      viewer.setAttribute('camera-orbit','0deg 78deg 55%');
    }else{
      viewer.setAttribute('camera-target',hasExtra?'auto auto auto':'0m 0.09m 0m');
      viewer.setAttribute('camera-orbit',`25deg 72deg ${hasExtra?110:s.armrest.type!=='none'?100:95}%`);
      viewer.setAttribute('field-of-view','30deg');
    }
    // Dragging changes the internal camera goal without changing its attributes.
    // Recompute even when the starting settings are identical, then jump after Lit updates.
    for(const name of ['cameraOrbit','cameraTarget','fieldOfView'])viewer.requestUpdate?.(name);
    await viewer.updateComplete;
    viewer.jumpCameraToGoal?.();requestFrame();
  }
  const requestFrame=()=>{const s=getScene();if(!s)return;if(s.queueRender)s.queueRender();else s.isDirty=true;};
  function modelUrl(){return modelFor(mode,config,store.getState().fabricType);}
  function announce(text){if(status)status.textContent=text;}
  function syncControls(){
    const active=currentMode(),microsuede=store.getState().fabricType==='microsuede';
    root.dataset.realismMode=active;
    panel?.querySelectorAll('[data-realism-mode]').forEach(b=>{
      b.setAttribute('aria-pressed',String(b.dataset.realismMode===active));
      b.disabled=microsuede&&b.dataset.realismMode!=='original';
    });
    if(!loading)announce(microsuede?'Microsuede uses the original model, fabric and lighting.':active==='original'?'Original corduroy preview':active==='materials'?'Updated corduroy fabric and lighting, original cushion shape':'Updated corduroy fabric, lighting and refined cushion shape');
  }
  function setLighting(){
    if(currentMode()==='original'){
      viewer.removeAttribute('environment-image');viewer.setAttribute('shadow-intensity','0');viewer.setAttribute('exposure','1');
    }else{
      // This older viewer recognises HDR only when the URL ends in .hdr.
      // Shopify's asset_url adds a version query, which otherwise selects
      // the ordinary image loader and leaves the model unlit.
      viewer.setAttribute('environment-image',config.realism.environmentUrl.split('?')[0]);
      viewer.setAttribute('shadow-intensity','0.9');viewer.setAttribute('shadow-softness','0.9');viewer.setAttribute('exposure','1');
    }
    // The old original poster is missing; do not show the rejected microsuede render.
    viewer.removeAttribute('poster');
  }
  function texture(url,srgb){
    if(cache.has(url))return Promise.resolve(cache.get(url));
    if(pending.has(url))return pending.get(url);
    const p=new Promise((resolve,reject)=>{
      const loader=new THREE.TextureLoader();loader.setCrossOrigin('anonymous');
      loader.load(url,t=>{t.flipY=false;t.wrapS=t.wrapT=THREE.RepeatWrapping;t.encoding=srgb?THREE.sRGBEncoding:THREE.LinearEncoding;t.anisotropy=4;t.needsUpdate=true;cache.set(url,t);pending.delete(url);resolve(t);},undefined,()=>{pending.delete(url);reject(new Error('Fabric texture unavailable'));});
    });pending.set(url,p);return p;
  }
  async function ensureFabric(fabric){
    if(fabric!=='corduroy'||currentMode()==='original')return;
    const urls=config.realism.fabrics[fabric];if(!urls)return;
    try{
      await Promise.all([texture(urls.colour,true),texture(urls.normal,false),texture(urls.orm,false)]);
      if(currentMode()!=='original'&&store.getState().fabricType===fabric){decorateAll();requestFrame();}
    }catch(error){announce('Fabric detail could not load. Your selections are saved; try Original preview.');}
  }
  function decorateNode(node,fabric,colour,hex){
    if(currentMode()==='original'||loading||!node?.material||!fabric||!hex)return;
    // Late promises from a previous selection must not repaint the current one.
    const selected=store.getState(),key=colourKeyForNode(node.name,selected);
    if(key){fabric=selected.fabricType;hex=config.realism.colours?.[key]||palette.get(key);}
    if(fabric!=='corduroy')return;
    const urls=config.realism.fabrics[fabric],profile=profileFor(fabric);
    if(!urls)return;
    if(!node.userData.ccPhysical){
      const old=node.material;
      const material=new THREE.MeshPhysicalMaterial({side:old.side,map:old.map,roughness:profile.roughness,metalness:0});
      material.morphTargets=Boolean(node.morphTargetInfluences);material.morphNormals=Boolean(node.geometry?.morphAttributes?.normal?.length);
      node.material=material;node.userData.ccPhysical=true;
    }
    const m=node.material;
    // Config hex codes are display colours. Convert explicitly for the r128
    // linear renderer so pale colours do not wash out.
    m.color.set(hex).convertSRGBToLinear();m.metalness=0;m.metalnessMap=null;
    m.roughness=profile.roughness;m.aoMap=null;m.envMapIntensity=1;
    m.reflectivity=.18;m.sheen=new THREE.Color(profile.sheen,profile.sheen,profile.sheen);
    if(cache.has(urls.colour))m.map=cache.get(urls.colour);
    if(cache.has(urls.normal)){m.normalMap=cache.get(urls.normal);m.normalScale.set(profile.normalStrength,profile.normalStrength);}
    if(cache.has(urls.orm))m.roughnessMap=cache.get(urls.orm);
    m.needsUpdate=true;
    if(node.morphTargetInfluences){const i=node.morphTargetDictionary?.Upholstery??0;node.morphTargetInfluences[i]=currentMode()==='refined'?1:0;}
  }
  function decorateAll(){
    if(currentMode()==='original'||!nodes)return;
    const state=store.getState();
    for(const [name,node] of nodes){const key=colourKeyForNode(name,state);if(key)decorateNode(node,state.fabricType,key,palette.get(key));}
  }
  function updateGround(){
    if(currentMode()==='original'||!nodes)return;
    const s=getScene(),shadow=s?.shadow;
    if(!shadow?.floor)return;
    s.updateMatrixWorld(true);
    let lowest=Infinity;
    for(const node of nodes.values()){
      if(node.visible&&node.isMesh&&node.geometry)lowest=Math.min(lowest,new THREE.Box3().setFromObject(node).min.y);
    }
    if(Number.isFinite(lowest)){
      const p=shadow.worldToLocal(new THREE.Vector3(0,lowest-.001,0));
      shadow.floor.position.y=p.y;shadow.needsUpdate=true;
    }
  }
  function onModelLoad(newNodes,reapply){
    clearTextOverlays();
    nodes=newNodes;repaint=reapply;loading=false;
    setLighting();syncControls();
    if(currentMode()!=='original'){ensureFabric(store.getState().fabricType);decorateAll();}
    requestFrame();
  }
  function clearTextOverlays(){
    const s=getScene(),stale=[];
    if(!s)return;
    s.traverse(o=>{
      if(o.name?.endsWith('_TextOverlay'))stale.push(o);
      if(o.userData?._textOverlay)delete o.userData._textOverlay;
    });
    for(const o of stale){
      o.parent?.remove(o);o.geometry?.dispose();o.material?.map?.dispose();o.material?.dispose();
      nodes?.delete(o.name);
    }
  }
  function syncSource(){
    setLighting();syncControls();
    if(viewer.getAttribute('src')!==modelUrl()){
      loading=true;announce('Loading preview. Your fabric and colour choices are saved.');
      clearTextOverlays();
      nodes=null;
      viewer.setAttribute('src',modelUrl());
    }else{decorateAll();repaint?.();requestFrame();}
  }
  function setMode(next){
    mode=normaliseMode(next);
    const u=new URL(location.href);u.searchParams.set('cc_view',mode);history.replaceState({},'',u);
    syncSource();
  }
  panel?.addEventListener('click',event=>{const button=event.target.closest('button[data-realism-mode]');if(button)setMode(button.dataset.realismMode);});
  function revealViewer(){
    const header=document.querySelector('.shopify-section-header');
    const inset=Math.max(0,header?.getBoundingClientRect().bottom||0)+12;
    const top=viewer.getBoundingClientRect().top;
    if(top<inset||top>innerHeight/2)window.scrollTo({top:Math.max(0,scrollY+top-inset),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
  }
  root.querySelector('[data-realism-camera]')?.addEventListener('click',()=>{
    resetCamera();
    revealViewer();
  });
  root.querySelector('[data-realism-detail]')?.addEventListener('click',()=>{
    viewer.setAttribute('camera-target','0m 0.09m 0m');viewer.setAttribute('camera-orbit','18deg 67deg 65%');viewer.jumpCameraToGoal?.();
    revealViewer();
  });
  viewer.addEventListener('error',()=>{
    if(currentMode()!=='original'){
      mode='original';setLighting();syncControls();viewer.setAttribute('src',config.model.glbUrl);
      announce('Enhanced preview could not load. Original preview restored; your selections are saved.');
    }
  });
  let previousFabric=store.getState().fabricType;
  store.subscribe(state=>{
    if(state.fabricType!==previousFabric){previousFabric=state.fabricType;syncSource();resetCamera();}
    if(currentMode()!=='original')ensureFabric(state.fabricType);
    const parts=`${state.armrest.type}|${state.extraFun.type}|${state.dotCushion.enabled}`;
    if(parts!==framedParts){framedParts=parts;resetCamera();}
  });
  const initial=store.getState();framedParts=`${initial.armrest.type}|${initial.extraFun.type}|${initial.dotCushion.enabled}`;
  resetCamera();
  setLighting();syncControls();
  if(currentMode()!=='original')ensureFabric(store.getState().fabricType);
  return {modelUrl,onModelLoad,decorateNode,decorateAll,updateGround,setMode,get mode(){return currentMode();}};
}
if(typeof window!=='undefined')window.FunsquareRealism={createRealism};
